import { apiCall } from 'providers';
import {
  createContext, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { toast } from 'react-toastify';
import { DriSchedListResponse } from '../types';

export interface DriProgramming {
  [DRI_ID: string]: DriSchedListResponse;
}

interface SchedsContextProps {
  loading: boolean;
  scheds: DriProgramming;
  fetchScheds: (driIds: string[]) => Promise<void>;
  clearSavedProgramming: (driIds: string[]) => void;
  refetchScheds: (driIds: string[]) => Promise<void>;
}

const DriProgrammingContext = createContext<SchedsContextProps | undefined>(undefined);

export const useDriProgrammingContext = (): SchedsContextProps => {
  const context = useContext(DriProgrammingContext);

  if (!context) {
    throw new Error('useDriProgrammingContext must be used within a DriProgrammingProvider');
  }

  return context;
};

export const DriProgrammingProvider = (
  { children }: { children: ReactNode },
): ReactElement => {
  const [driProgramming, setDriProgramming] = useState<DriProgramming>({});
  const [loading, setLoading] = useState(false);
  const timeouts = useRef<Record<string, NodeJS.Timeout>>({});
  const retryInterval = 5 * 60 * 1000;

  function deleteTimeoutByDriIds(driIds: string[]): void {
    for (const driId of driIds) {
      const timeout = timeouts.current[driId];
      if (timeout) {
        clearTimeout(timeout);
        delete timeouts.current[driId];
      }
    }
  }

  const fetchScheds = useCallback(async (driIds: string[]) => {
    try {
      setLoading(true);
      const promises = driIds.map((driId) => apiCall('/dri/get-dri-scheds', { DRI_ID: driId }).then((value) => ({
        driId,
        response: value,
      })));

      const responses = await Promise.all(promises);

      setDriProgramming((previousValues) => {
        const newProgramming: DriProgramming = { ...previousValues };

        for (const { driId, response } of responses) {
          newProgramming[driId] = response.list;
        }
        return newProgramming;
      });

      const timeout = setTimeout(() => {
        deleteTimeoutByDriIds(driIds);
        fetchScheds(driIds);
      }, retryInterval);

      for (const driId of driIds) {
        timeouts.current[driId] = timeout;
      }

      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao buscar programações.');
    }
  }, [retryInterval]);

  const clearSavedProgramming = useCallback((driIds: string[]): void => {
    setDriProgramming((previousValues) => {
      if (driIds.length === 0) return {};
      const newProgramming = { ...previousValues };

      deleteTimeoutByDriIds(driIds);

      for (const driId of driIds) {
        delete newProgramming[driId];
      }

      return newProgramming;
    });
  }, []);

  const refetchScheds = useCallback(async (driIds: string[]): Promise<void> => {
    clearSavedProgramming(driIds);

    await fetchScheds(driIds);
  }, [clearSavedProgramming, fetchScheds]);

  const clearTimeouts = (): void => {
    for (const value of Object.values(timeouts.current)) {
      clearTimeout(value);
    }
  };

  const values = useMemo((): SchedsContextProps => ({
    fetchScheds,
    loading,
    clearSavedProgramming,
    scheds: driProgramming,
    refetchScheds,
  }), [clearSavedProgramming, driProgramming, fetchScheds, loading, refetchScheds]);

  useEffect(() => () => {
    clearTimeouts();
  }, []);

  return (
    <DriProgrammingContext.Provider value={values}>
      {children}
    </DriProgrammingContext.Provider>
  );
};
